import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Fade,
  Card,
  CardContent,
  CardActions,
  InputAdornment,
  TextField,
  Grid,
  Divider,
  Snackbar,
  useTheme,
  Zoom,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Pagination,
  Checkbox,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  DirectionsCar as CarIcon,
  Search as SearchIcon,
  Sort as SortIcon,
  Speed as SpeedIcon,
  AttachMoney as MoneyIcon,
  CalendarMonth as CalendarIcon,
  DateRange as DateIcon,
  GridOn as ExcelIcon,
  PhotoLibrary as BannerIcon,
} from '@mui/icons-material';
import { animated } from '@react-spring/web';
import vehicleService from '../../services/vehicleService';
import { pdf } from '@react-pdf/renderer';
import VehiclesReportPDF from './VehiclesReportPDF';
import { exportVehiclesReportExcel } from '../../utils/reportExcelExports';
import { useAuthUser } from '../../contexts/AuthUserContext';
import InstagramBannerDialog from './instagramBanner/InstagramBannerDialog';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  year: number;
  plate?: string;
  no_plate?: boolean;
  identification?: string;
  km?: string;
  color: string;
  purchase_price: number;
  sale_price?: number;
  sale_price_promo?: number;
  purchase_date: string;
  status: 'available' | 'sold' | 'maintenance';
  images?: { id: number, url: string }[];
  is_consignado?: boolean;
}

const AnimatedTableRow = animated(TableRow);
const AnimatedCard = animated(Card);

type VehicleTabStatus = 'available' | 'maintenance' | 'sold';

type StatusTab = { status: VehicleTabStatus; param: string; label: string };

const STATUS_TABS: StatusTab[] = [
  { status: 'available', param: 'disponiveis', label: 'Disponíveis' },
  { status: 'maintenance', param: 'manutencao', label: 'Em manutenção' },
  { status: 'sold', param: 'vendidos', label: 'Vendidos' },
];

// Veículo antigo sem status definido fica em Disponíveis para não sumir da tela.
const tabStatusOf = (vehicle: { status?: string | null }): VehicleTabStatus =>
  vehicle.status === 'sold' || vehicle.status === 'maintenance' ? vehicle.status : 'available';

const VehicleList: React.FC = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuthUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'price_high' | 'price_low'>('newest');
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortMenuAnchorEl, setSortMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bannerOpen, setBannerOpen] = useState(false);

  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const getFullImageUrl = (url?: string) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    if (!API_BASE) return url;
    return `${API_BASE}${url.startsWith('/') ? url : `/${url}`}`;
  };

  const openSortMenu = Boolean(sortMenuAnchorEl);
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // Para armazenar a mensagem de sucesso
  const [openSnackbar, setOpenSnackbar] = useState(false); // Controla a visibilidade do Snackbar

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortMenuAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortMenuAnchorEl(null);
  };

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        setLoading(true);

        // Usando o serviço real para buscar os veículos
        const data = await vehicleService.getAllVehicles();
        setVehicles(data);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar veículos:', error);
        setError('Erro ao carregar veículos. Tente novamente.');
        setLoading(false);
      }
    };

    fetchVehicles();
  }, []);

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const now = new Date();
      const generatedAtLabel = now.toLocaleString('pt-BR');
      const vehiclesForReport = vehicles.filter(v => v.status !== 'sold');

      const doc = (
        <VehiclesReportPDF
          vehicles={vehiclesForReport}
          generatedAtLabel={generatedAtLabel}
          getImageUrl={(v) => getFullImageUrl(v.images?.[0]?.url)}
        />
      );

      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `veiculos-${now.toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setDownloadingExcel(true);
      const now = new Date();
      const generatedAtLabel = now.toLocaleString('pt-BR');
      const vehiclesForReport = vehicles.filter((v) => v.status !== 'sold');
      await exportVehiclesReportExcel({ vehicles: vehiclesForReport, generatedAtLabel });
    } finally {
      setDownloadingExcel(false);
    }
  };

  useEffect(() => {
    // Automatically switch to card view on mobile devices
    if (isMobile) {
      setViewMode('card');
    } else {
      setViewMode('table');
    }
  }, [isMobile]);

  const handleAddVehicle = () => {
    navigate('/veiculos/novo');
  };

  const handleEditVehicle = (id: number) => {
    navigate(`/veiculos/editar/${id}`, { state: { listSearch: window.location.search } });
  };

  const handleDeleteVehicle = async (id: number) => {
    try {

      const confirm = window.confirm('Tem certeza que deseja excluir o veículo?')

      if (confirm) {
        const data = await vehicleService.deleteVehicle(id);

        setVehicles(vehicles.filter(item => item.id !== id));

        if (data === 204) {
          setSuccessMessage('Veiculo excluíd com sucesso!');
          setOpenSnackbar(true); 

          setTimeout(() => {
            setOpenSnackbar(false);
            setSuccessMessage(null);
          }, 3000);
        }
      }
    } catch (error) {
      alert('Erro ao excluir a despesa')
      console.error('Erro ao excluir a despesa:', error);
    }

  };

  const handleViewVehicle = (id: number) => {
    navigate(`/veiculos/${id}`, { state: { listSearch: window.location.search } });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'sold':
        return 'primary';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available':
        return 'Disponível';
      case 'sold':
        return 'Vendido';
      case 'maintenance':
        return 'Em Manutenção';
      default:
        return status;
    }
  };

  // A aba fica no endereço da página para continuar nela ao voltar de um veículo.
  const activeTab = STATUS_TABS.find((tab) => tab.param === searchParams.get('aba')) ?? STATUS_TABS[0];

  const changeTab = (tab: StatusTab) => {
    setSearchParams(tab.status === STATUS_TABS[0].status ? {} : { aba: tab.param }, { replace: true });
    setPage(1);
    setSelectedIds([]);
  };

  const matchesSearch = (vehicle: Vehicle) => {
    const term = searchTerm.toLowerCase();
    return (
      vehicle.brand.toLowerCase().includes(term) ||
      vehicle.model.toLowerCase().includes(term) ||
      String(vehicle.plate || '').toLowerCase().includes(term) ||
      String(vehicle.identification || '').toLowerCase().includes(term)
    );
  };

  const searchResults = vehicles.filter(matchesSearch);
  const countInTab = (tab: StatusTab) => searchResults.filter((v) => tabStatusOf(v) === tab.status).length;

  // Filtrar veículos com base no termo de pesquisa e na aba
  const filteredVehicles = searchResults
    .filter((vehicle) => tabStatusOf(vehicle) === activeTab.status)
    .sort((a, b) => {
      // Ordenação dos veículos
      switch (sortOrder) {
        case 'newest':
          // Ordenar por data de compra (mais recente primeiro)
          return new Date(b.purchase_date).getTime() - new Date(a.purchase_date).getTime();
        case 'oldest':
          // Ordenar por data de compra (mais antigo primeiro)
          return new Date(a.purchase_date).getTime() - new Date(b.purchase_date).getTime();
        case 'price_high':
          // Ordenar por preço (mais caro primeiro)
          return b.purchase_price - a.purchase_price;
        case 'price_low':
          // Ordenar por preço (mais barato primeiro)
          return a.purchase_price - b.purchase_price;
        default:
          return 0;
      }
    });

  // Calcular veículos paginados
  const paginatedVehicles = filteredVehicles.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Calcular número total de páginas
  const totalPages = Math.ceil(filteredVehicles.length / rowsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const toggleSelectVehicle = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const pageIds = paginatedVehicles.map((v) => v.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));
  const somePageSelected = pageIds.some((id) => selectedIds.includes(id));

  const toggleSelectPage = () => {
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const selectedVehicles = vehicles.filter((v) => selectedIds.includes(v.id));

  // Estilos de animação para itens
  const rowAnimationStyle = {
    opacity: 1,
    transform: 'translateY(0)'
  };

  // Estilos de animação para cards
  const cardAnimationStyle = {
    opacity: 1,
    transform: 'scale(1)'
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 4,
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 }
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Veículos
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gerencie e monitore todos os veículos da sua frota
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {canWrite && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddVehicle}
                sx={{
                  py: 1.2,
                  px: 3,
                  borderRadius: '8px',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)',
                  }
                }}
              >
                Adicionar Veículo
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<BannerIcon />}
              onClick={() => setBannerOpen(true)}
              disabled={selectedIds.length === 0}
              sx={{ borderRadius: '8px' }}
            >
              Banner Instagram{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
            </Button>
            <Button
              variant="outlined"
              color="success"
              onClick={() => void handleDownloadExcel()}
              disabled={downloadingExcel || vehicles.filter(v => v.status !== 'sold').length === 0}
              sx={{ borderRadius: '8px' }}
            >
              {downloadingExcel ? 'Gerando Excel...' : 'Baixar Excel'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf || vehicles.filter(v => v.status !== 'sold').length === 0}
              sx={{ borderRadius: '8px' }}
            >
              {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
            </Button>
          </Box>
        </Box>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}
          sx={{
            marginTop: '55px',
            zIndex: 9,
          }}

        >
          <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
        <Tabs
          value={activeTab.status}
          onChange={(_, status) => changeTab(STATUS_TABS.find((tab) => tab.status === status) ?? STATUS_TABS[0])}
          variant="scrollable"
          allowScrollButtonsMobile
          aria-label="Situação dos veículos"
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          {STATUS_TABS.map((tab) => (
            <Tab key={tab.status} value={tab.status} label={`${tab.label} (${countInTab(tab)})`} />
          ))}
        </Tabs>

        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 4,
            borderRadius: '12px',
            backdropFilter: 'blur(4px)',
            background: theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.8)'
              : 'rgba(255, 255, 255, 0.8)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Buscar por marca, modelo ou placa..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(0, 0, 0, 0.2)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  startIcon={<SortIcon />}
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: '8px' }}
                  onClick={handleSortClick}
                >
                  {sortOrder === 'newest' ? 'Mais Recentes' :
                    sortOrder === 'oldest' ? 'Mais Antigos' :
                      sortOrder === 'price_high' ? 'Maior Preço' : 'Menor Preço'}
                </Button>
                <Menu
                  anchorEl={sortMenuAnchorEl}
                  open={openSortMenu}
                  onClose={handleSortClose}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      borderRadius: '8px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  <MenuItem
                    onClick={() => { setSortOrder('newest'); handleSortClose(); }}
                    selected={sortOrder === 'newest'}
                  >
                    <ListItemIcon>
                      <DateIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Mais Recentes</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => { setSortOrder('oldest'); handleSortClose(); }}
                    selected={sortOrder === 'oldest'}
                  >
                    <ListItemIcon>
                      <DateIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Mais Antigos</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => { setSortOrder('price_high'); handleSortClose(); }}
                    selected={sortOrder === 'price_high'}
                  >
                    <ListItemIcon>
                      <MoneyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Maior Preço</ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => { setSortOrder('price_low'); handleSortClose(); }}
                    selected={sortOrder === 'price_low'}
                  >
                    <ListItemIcon>
                      <MoneyIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Menor Preço</ListItemText>
                  </MenuItem>
                </Menu>
                <Tooltip title={viewMode === 'table' ? 'Visualização em Cards' : 'Visualização em Tabela'}>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: '8px', minWidth: '40px', width: '40px' }}
                    onClick={() => setViewMode(viewMode === 'table' ? 'card' : 'table')}
                  >
                    <GridViewIcon />
                  </Button>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '8px' }}>
            {error}
          </Alert>
        )}

        {filteredVehicles.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: 'center', borderRadius: '12px' }}>
            <CarIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.6 }} />
            <Typography variant="h6">Nenhum veículo encontrado</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '500px', mx: 'auto' }}>
              {searchTerm
                ? `Nenhum resultado em ${activeTab.label} para esta busca.`
                : `Não há veículos em ${activeTab.label} no momento.`}
            </Typography>
            {STATUS_TABS.filter((tab) => tab.status !== activeTab.status && searchTerm && countInTab(tab) > 0).map((tab) => (
              <Button key={tab.status} onClick={() => changeTab(tab)} sx={{ mb: 2, display: 'block', mx: 'auto' }}>
                Ver {countInTab(tab)} em {tab.label}
              </Button>
            ))}
            {canWrite && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddVehicle}
                sx={{
                  py: 1.2,
                  borderRadius: '8px',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                }}
              >
                Adicionar Veículo
              </Button>
            )}
          </Paper>
        ) : viewMode === 'table' ? (
          <>
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                mb: 3
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(0, 0, 0, 0.2)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={somePageSelected && !allPageSelected}
                        checked={allPageSelected}
                        onChange={toggleSelectPage}
                        inputProps={{ 'aria-label': 'Selecionar página' }}
                      />
                    </TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>Imagem</TableCell>
                    <TableCell>Marca/Modelo</TableCell>
                    <TableCell>Ano</TableCell>
                    <TableCell>Placa</TableCell>
                    <TableCell>Preço de Compra</TableCell>
                    <TableCell>Preço de Venda</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedVehicles.map((vehicle, index) => (
                    <AnimatedTableRow
                      key={vehicle.id}
                      hover
                      style={{
                        ...rowAnimationStyle,
                        transition: `all 0.3s ease ${index * 0.1}s`
                      }}
                      sx={{
                        '&:hover': {
                          bgcolor: 'rgba(63, 136, 197, 0.04)',
                          cursor: 'pointer',
                        },
                        bgcolor: selectedIds.includes(vehicle.id)
                          ? 'rgba(221, 47, 48, 0.06)'
                          : undefined,
                      }}
                      onClick={() => handleViewVehicle(vehicle.id)}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(vehicle.id)}
                          onChange={() => toggleSelectVehicle(vehicle.id)}
                          inputProps={{ 'aria-label': `Selecionar veículo ${vehicle.id}` }}
                        />
                      </TableCell>
                      <TableCell>{vehicle.id}</TableCell>
                      <TableCell>
                        {vehicle.images && vehicle.images.length > 0 ? (
                          <Box
                            component="img"
                            src={vehicle.images[0].url}
                            alt={`${vehicle.brand} ${vehicle.model}`}
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '8px',
                              objectFit: 'cover',
                              border: '1px solid',
                              borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '8px',
                              backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <CarIcon color="disabled" />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{`${vehicle.brand} ${vehicle.model}`}</TableCell>
                      <TableCell>{vehicle.year}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                        {vehicle.identification
                          ? vehicle.identification
                          : (vehicle.no_plate ? 'Sem placa' : (vehicle.plate || '—'))}
                      </TableCell>
                      <TableCell>{`R$ ${ vehicle.is_consignado ? 'N/A' : vehicle.purchase_price?.toLocaleString()}`}</TableCell>
                      <TableCell>
                        {vehicle.sale_price != null
                          ? `R$ ${Number(vehicle.sale_price).toLocaleString()}`
                          : '—'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                          <Chip
                            label={getStatusLabel(vehicle.status)}
                            color={getStatusColor(vehicle.status) as any}
                            size="small"
                            sx={{
                              fontWeight: 'bold',
                              boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                          {vehicle.is_consignado && (
                            <Chip
                              label="Consignado"
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontWeight: 'medium' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Tooltip title="Detalhes" arrow>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewVehicle(vehicle.id);
                              }}
                              sx={{
                                mx: 0.5,
                                transition: 'all 0.2s',
                                '&:hover': { transform: 'translateY(-2px)' }
                              }}
                            >
                              <InfoIcon />
                            </IconButton>
                          </Tooltip>
                          {canWrite && (
                            <>
                              <Tooltip title="Editar" arrow>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditVehicle(vehicle.id);
                                  }}
                                  sx={{
                                    mx: 0.5,
                                    transition: 'all 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)' }
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir" arrow>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteVehicle(vehicle.id);
                                  }}
                                  sx={{
                                    mx: 0.5,
                                    transition: 'all 0.2s',
                                    '&:hover': { transform: 'translateY(-2px)' }
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </AnimatedTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    margin: '0 4px',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <>
            <Grid container spacing={3}>
              {paginatedVehicles.map((vehicle, index) => (
                <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
                  <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                    <Box>
                      <AnimatedCard
                        style={{
                          ...cardAnimationStyle,
                          transition: `all 0.3s ease ${index * 0.1}s`
                        }}
                        sx={{
                          borderRadius: '16px',
                          overflow: 'hidden',
                          transition: 'all 0.3s',
                          cursor: 'pointer',
                          position: 'relative',
                          outline: selectedIds.includes(vehicle.id)
                            ? '2px solid #DD2F30'
                            : 'none',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 20px rgba(0, 0, 0, 0.3)'
                          }
                        }}
                        onClick={() => handleViewVehicle(vehicle.id)}
                      >
                        <Checkbox
                          checked={selectedIds.includes(vehicle.id)}
                          onChange={() => toggleSelectVehicle(vehicle.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            zIndex: 2,
                            bgcolor: 'rgba(255,255,255,0.85)',
                            borderRadius: 1,
                            p: 0.25,
                          }}
                          inputProps={{ 'aria-label': `Selecionar veículo ${vehicle.id}` }}
                        />
                        {/* Exibição da imagem na parte superior do card */}
                        {vehicle.images && vehicle.images.length > 0 ? (
                          <Box
                            sx={{
                              position: 'relative',
                              height: 180,
                              width: '100%',
                              overflow: 'hidden'
                            }}
                          >
                            <Box
                              component="img"
                              src={vehicle.images[0].url}
                              alt={`${vehicle.brand} ${vehicle.model}`}
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '50%',
                                background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                              }}
                            />
                            <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={getStatusLabel(vehicle.status)}
                                color={getStatusColor(vehicle.status) as any}
                                size="small"
                                sx={{ fontWeight: 'bold', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)' }}
                              />
                              {vehicle.is_consignado && (
                                <Chip label="Consignado" size="small" color="secondary" variant="outlined" sx={{ fontWeight: 'medium' }} />
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: 180,
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                              position: 'relative'
                            }}
                          >
                            <CarIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                            <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              <Chip
                                label={getStatusLabel(vehicle.status)}
                                color={getStatusColor(vehicle.status) as any}
                                size="small"
                                sx={{ fontWeight: 'bold', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.2)' }}
                              />
                              {vehicle.is_consignado && (
                                <Chip label="Consignado" size="small" color="secondary" variant="outlined" sx={{ fontWeight: 'medium' }} />
                              )}
                            </Box>
                          </Box>
                        )}
                        <CardContent>
                          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {`${vehicle.brand} ${vehicle.model}`}
                          </Typography>

                          <Grid container spacing={2} sx={{ mb: 1 }}>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CalendarIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Ano: <Box component="span" sx={{ color: 'text.primary', fontWeight: 'medium' }}>{vehicle.year}</Box>
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <SpeedIcon fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                  Identificação:{' '}
                                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 'medium', fontFamily: 'monospace' }}>
                                    {vehicle.identification
                                      ? vehicle.identification
                                      : (vehicle.no_plate ? 'Sem placa' : (vehicle.plate || '—'))}
                                  </Box>
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          <Divider sx={{
                            my: 1.5,
                            borderColor: theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.1)'
                              : 'rgba(0,0,0,0.1)'
                          }} />

                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <MoneyIcon fontSize="small" sx={{ color: theme.palette.success.main, mr: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              Preço de compra:
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" sx={{ ml: 'auto', color: theme.palette.success.main }}>
                              R$ { vehicle.is_consignado ? 'N/A' : vehicle.purchase_price?.toLocaleString()}
                            </Typography>
                          </Box>
                        </CardContent>
                        <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between' }}>
                          <Box>
                            {canWrite && (
                              <>
                                <Tooltip title="Editar" arrow>
                                  <IconButton
                                    color="primary"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEditVehicle(vehicle.id);
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Excluir" arrow>
                                  <IconButton
                                    color="error"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteVehicle(vehicle.id);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewVehicle(vehicle.id);
                            }}
                          >
                            Ver Detalhes
                          </Button>
                        </CardActions>
                      </AnimatedCard>
                    </Box>
                  </Zoom>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    margin: '0 4px',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                      },
                    },
                  },
                  '& .MuiPaginationItem-ellipsis': {
                    borderRadius: '8px',
                  },
                }}
              />
            </Box>
          </>
        )}

        <InstagramBannerDialog
          open={bannerOpen}
          onClose={() => setBannerOpen(false)}
          vehicles={selectedVehicles}
          getImageUrl={getFullImageUrl}
        />
      </div>
    </Fade>
  );
};

export default VehicleList;

// Componente do ícone de grid para altenar visualização
const GridViewIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 3H10V10H3V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 3H21V10H14V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 14H10V21H3V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 14H21V21H14V14Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
); 